import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import { extractErrorMessage } from '../../utils/apitools'
import useAccount from '../useAccount'
import { API_BASE_URL } from '../../constants/system'

/**
 * One department's chooser card.
 *
 * Deliberately no campaign ids: the server does not send them, because which
 * campaigns make up a department is Sky-internal and the chooser is the page
 * every department loads. If this type ever grows a `campaigns` field, something
 * has been added to the API that should not have been.
 */
export interface SkyProduct {
  slug: string
  name: string
  description: string
  /**
   * The ingest source the SERVER pins this product's requests to, or absent
   * when the viewer may choose. See `sourceIsSelectable`.
   */
  sourceScope?: string
}

/**
 * May this product's viewer actually choose an ingest source?
 *
 * 🔴 WHY THE SERVER ANSWERS THIS. Every department dashboard rendered a live
 * source toggle whose choice the server then replaced with `sprinklr`
 * (forceSourceScope): the user clicked Apify, every panel refetched, and
 * identical numbers came back. An inert control is worse than a hidden default
 * — it teaches the user that the data does not change, when their question was
 * never asked. The pin itself is right (a department is defined by campaign and
 * only Sprinklr carries one), so the control is what goes.
 *
 * The answer rides on the product card rather than living in a hardcoded list
 * of departments here, so the manifest stays the only place the rule is
 * written, and a product switched to `scope: selectable` gets its control back
 * without a frontend change.
 *
 * ⚠ UNKNOWN MEANS PINNED. The catalogue is fetched, so every dashboard renders
 * at least once before it arrives. Defaulting the other way would flash an
 * inert control onto all five departments on every load — precisely the thing
 * being removed — where this merely delays a real control on one page by one
 * request.
 */
export function sourceIsSelectable(product: string, products: SkyProduct[]): boolean {
  const card = products.find((p) => p.slug === product)
  return Boolean(card) && !card?.sourceScope
}

interface SkyProductsResponse {
  products: SkyProduct[]
}

interface UseSkyProductsResult {
  products: SkyProduct[]
  isLoading: boolean
  /** The catalogue has answered (or failed): whether a source is selectable is now known. */
  isSettled: boolean
  error: Error | null
}

/**
 * Hook for `GET /sky-social-media/products`.
 *
 * Note the URL: no customer, no job. The backend group fixes both, so there is
 * nothing for this hook to pass and nothing a caller could get wrong. The
 * catalogue changes only when the committed manifest does — i.e. on a deploy —
 * so it is cached for the session rather than refetched per mount.
 */
export function useSkyProducts(enabled = true): UseSkyProductsResult {
  const account = useAccount()

  const query = useQuery({
    queryKey: ['sky_social_products'],
    enabled: Boolean(account.user && enabled),
    staleTime: Infinity,
    queryFn: async (): Promise<SkyProduct[]> => {
      try {
        const res = await axios.get<SkyProductsResponse>(`${API_BASE_URL}/sky-social-media/products`)
        return res.data?.products ?? []
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  return {
    products: query.data ?? [],
    isLoading: query.isLoading,
    // Answered either way. A failed catalogue still settles, so nothing that
    // waits on this can wait for ever.
    isSettled: query.isSuccess || query.isError,
    error: query.error as Error | null,
  }
}

export default useSkyProducts
